import React, { useEffect, useRef } from "react";
import styles from "./styles.module.scss";
import TabButton from "../Button/TabButton";
import { useHistory, useLocation } from "react-router-dom";
import { cls } from "app/utils";

interface ITab {
  label: string;
  path?: string;
  onClickHandler?: () => void;
  isActive?: boolean;
}

interface IProps {
  tabs: ITab[];
  /** On a phone, the tab bar scrolls sideways instead of wrapping. */
  scrollable?: boolean;
}

const Tabs = ({ tabs, scrollable }: IProps) => {
  const history = useHistory();
  const location = useLocation();
  const wrapperRef = useRef<HTMLDivElement>(null);

  const route = (id: string) => {
    history.push(id);
  };

  const defaultHandler = (path: string) => {
    route(path);
  };

  const handleClick = (tab: ITab) => {
    return tab.onClickHandler ? tab.onClickHandler() : defaultHandler(tab.path);
  };

  const isActive = (tab: ITab) => {
    return tab.isActive ?? location.pathname.includes(tab.path);
  };

  const activeIndex = tabs.findIndex(isActive);

  // A scrollable bar moves the active tab into view, so a tab at the end of
  // a narrow bar is not hidden when its page opens. Only the bar scrolls,
  // not the page.
  useEffect(() => {
    const bar = wrapperRef.current;
    const tab = bar?.children[activeIndex] as HTMLElement | undefined;
    if (!scrollable || !bar || !tab) {
      return;
    }
    const barBox = bar.getBoundingClientRect();
    const tabBox = tab.getBoundingClientRect();
    if (tabBox.right > barBox.right) {
      bar.scrollLeft += tabBox.right - barBox.right;
    } else if (tabBox.left < barBox.left) {
      bar.scrollLeft += tabBox.left - barBox.left;
    }
  }, [scrollable, activeIndex, tabs.length]);

  return (
    <div
      ref={wrapperRef}
      className={cls(styles.tabs_wrapper, {
        [styles.scrollable]: scrollable,
      })}
    >
      {tabs.map((tab) => {
        return (
          <TabButton
            key={tab.label}
            label={tab.label}
            isActive={isActive(tab)}
            onClick={() => {
              handleClick(tab);
            }}
          />
        );
      })}
    </div>
  );
};

export default Tabs;
